const ENCRYPTION_KEY = import.meta.env.VITE_STORAGE_ENCRYPTION_KEY;

/**
 * Derives a crypto key from a passphrase using PBKDF2
 */
async function deriveKey(passphrase: string): Promise<CryptoKey> {
  const encoder = new TextEncoder();
  const keyMaterial = await crypto.subtle.importKey(
    'raw',
    encoder.encode(passphrase),
    'PBKDF2',
    false,
    ['deriveKey']
  );

  return crypto.subtle.deriveKey(
    {
      name: 'PBKDF2',
      salt: encoder.encode('helloheart-salt'), // Static salt
      iterations: 100000,
      hash: 'SHA-256',
    },
    keyMaterial,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt']
  );
}

/**
 * Converts Sets to Arrays for JSON serialization
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function prepareForSerialization(obj: any): any {
  if (obj instanceof Set) {
    return Array.from(obj);
  }
  if (Array.isArray(obj)) {
    return obj.map(prepareForSerialization);
  }
  if (obj !== null && typeof obj === 'object') {
    const result: Record<string, unknown> = {};
    for (const key in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
        result[key] = prepareForSerialization(obj[key]);
      }
    }
    return result;
  }
  return obj;
}

/**
 * Encrypts data using AES-GCM
 * @param data - Data to encrypt (will be JSON stringified)
 * @returns Base64-encoded encrypted data with IV prepended
 */
export async function encryptData<T>(data: T): Promise<string> {
  try {
    const encoder = new TextEncoder();
    const key = await deriveKey(ENCRYPTION_KEY);

    // Generate random initialization vector
    const iv = crypto.getRandomValues(new Uint8Array(12));

    // Convert Sets to Arrays before serialization
    const serializable = prepareForSerialization(data);

    // Encrypt the data
    const encryptedBuffer = await crypto.subtle.encrypt(
      { name: 'AES-GCM', iv },
      key,
      encoder.encode(JSON.stringify(serializable))
    );

    // Combine IV and encrypted data
    const combined = new Uint8Array(iv.length + encryptedBuffer.byteLength);
    combined.set(iv, 0);
    combined.set(new Uint8Array(encryptedBuffer), iv.length);

    // Convert to base64 for storage
    return btoa(String.fromCharCode(...combined));
  } catch (error) {
    console.error('Encryption failed:', error);
    throw new Error('Failed to encrypt data');
  }
}

/**
 * Decrypts data encrypted with encryptData
 * @param encryptedData - Base64-encoded encrypted data
 * @returns Decrypted data
 */
export async function decryptData<T>(encryptedData: string): Promise<T> {
  try {
    const decoder = new TextDecoder();
    const key = await deriveKey(ENCRYPTION_KEY);

    // Decode from base64
    const combined = Uint8Array.from(atob(encryptedData), c => c.charCodeAt(0));

    // Extract IV and encrypted data
    const iv = combined.slice(0, 12);
    const encrypted = combined.slice(12);

    // Decrypt
    const decryptedBuffer = await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv },
      key,
      encrypted
    );

    // Parse JSON
    const decryptedText = decoder.decode(decryptedBuffer);
    return JSON.parse(decryptedText);
  } catch (error) {
    console.error('Decryption failed:', error);
    throw new Error('Failed to decrypt data');
  }
}

